import { ChangeEvent, MouseEvent, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Upload } from '@emotion-icons/heroicons-outline/Upload'
import { Close } from '@emotion-icons/material-rounded/Close'

import { TextField, TextFieldProps } from 'src/components/text-field'
import { Colors, Shapes } from 'src/components/theme'
import { fileToBase64 } from 'src/components/upload-field/utils'
import { growlError } from 'src/components/growl'

const FieldLabel = styled.label`
  position: relative;

  & input {
    cursor: pointer;
  }
`

const Content = styled.div`
  pointer-events: none;
  display: flex;
  align-items: center;
  position: absolute;
  top: ${Shapes.spacing[0]}px;
  bottom: ${Shapes.spacing[0]}px;
  left: ${Shapes.spacing[0]}px;
  right: ${Shapes.spacing[0]}px;
  padding-top: 6px;
`

const IconWrapper = styled.div`
  pointer-events: all;
  cursor: pointer;
  display: flex;
  padding: ${Shapes.spacing[2]}px;
`

const Placeholder = styled.div`
  flex: 1;
  padding: 0px ${Shapes.spacing[2]}px;
  color: ${Colors.blueDark};
  border-left: solid 1px ${Colors.borderInput};
`

const Logo = styled.img`
  width: ${Shapes.spacing[4]}px;
  height: ${Shapes.spacing[4]}px;
`

type UploadFieldProps = TextFieldProps & {
  label: string
  value?: string | null
  onChangeDataUri: (data?: string | null) => void
}

export const UploadField = (props: UploadFieldProps) => {
  const { label, value, placeholder, onChangeDataUri, ...textFieldProps } = props
  const [logoFile, setLogoFile] = useState<File>()
  const [logoDataUri, setLogoDataUri] = useState<string | null | undefined>(value)

  const fileFieldRef = useRef<HTMLInputElement>(null)

  const openFileSelector = () => fileFieldRef.current?.click()

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.item(0)
    if (file) {
      setLogoFile(file)
    }
  }

  const removeLogoFile = (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
    e.preventDefault()
    setLogoFile(undefined)
  }

  useEffect(() => {
    if (logoFile) {
      fileToBase64(logoFile).then(setLogoDataUri).catch(growlError)
    } else {
      if (fileFieldRef.current) {
        const dt = new DataTransfer()
        fileFieldRef.current.files = dt.files
      }
      setLogoDataUri(undefined)
    }
  }, [logoFile, fileFieldRef])

  useEffect(() => {
    onChangeDataUri(logoDataUri)
  }, [logoDataUri])

  useEffect(() => {
    setLogoDataUri(value)
  }, [value])

  return (
    <>
      <FieldLabel htmlFor="file">
        <TextField
          {...textFieldProps}
          label={label}
          value=" "
          onClick={openFileSelector}
          inputProps={{ readOnly: true }}
        />
        <Content>
          <IconWrapper>
            {logoDataUri ? <Logo src={logoDataUri} /> : <Upload color={Colors.blueDark} size={Shapes.spacing[4]} />}
          </IconWrapper>
          <Placeholder>{logoFile?.name || placeholder}</Placeholder>
          {logoFile && (
            <IconWrapper onClick={removeLogoFile}>
              <Close color={Colors.redDark} size={Shapes.spacing[4]} />
            </IconWrapper>
          )}
        </Content>
      </FieldLabel>
      <input
        id="file"
        ref={fileFieldRef}
        type="file"
        multiple={false}
        accept="image/*"
        onChange={handleFileSelected}
        hidden
      />
    </>
  )
}
